import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Stack, UploadSimple, Trash, CircleNotch, ArrowsClockwise, LockSimple,
} from "@phosphor-icons/react";

const LIB_CATS = ["survey", "drafting", "annotation", "points", "other"];

function lineDiff(a, b) {
  const A = (a || "").split("\n"), B = (b || "").split("\n");
  const n = A.length, m = B.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const rows = []; let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { rows.push({ t: "ctx", l: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { rows.push({ t: "del", l: A[i] }); i++; }
    else { rows.push({ t: "add", l: B[j] }); j++; }
  }
  while (i < n) rows.push({ t: "del", l: A[i++] });
  while (j < m) rows.push({ t: "add", l: B[j++] });
  return rows;
}

const ConvertDiff = ({ files }) => {
  const [idx, setIdx] = useState(0);
  const [published, setPublished] = useState(false);
  const f = files[Math.min(idx, files.length - 1)];
  useEffect(() => { setPublished(false); }, [idx]);
  const publishThis = async () => {
    if (!f || published) return;
    try {
      await api.publishPreset({ title: f.name.replace(/\.[^.]+$/, ""), command: (f.commands || [])[0] || "",
        code: f.code || "", platform: "AutoCAD Civil 3D", version: "2018" });
      setPublished(true);
      toast.success(`Published "${f.name}" — now a preset chip for every client`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };
  if (!f) return null;
  const rows = lineDiff(f.source, f.code);
  const adds = rows.filter((r) => r.t === "add").length;
  const dels = rows.filter((r) => r.t === "del").length;
  return (
    <div className="border border-[var(--border)] rounded-[4px] mt-2 flex flex-col min-h-0" data-testid="convert-diff">
      <div className="shrink-0 flex items-center gap-2 flex-wrap px-2.5 py-2 border-b border-[var(--border)] bg-[var(--surface-2)]">
        <span className="text-[11px] font-mono text-[var(--text-dim)] uppercase tracking-wider">Preview changes</span>
        {files.length > 1 && (
          <select value={idx} onChange={(e) => setIdx(Number(e.target.value))} data-testid="diff-file-select"
            className="h-7 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] text-[11px] font-mono px-1.5 focus:outline-none">
            {files.map((x, n) => <option key={n} value={n}>{x.name}</option>)}
          </select>
        )}
        <span className="text-[11px] font-mono">
          <span className="text-[var(--success)]">+{adds}</span>{" "}
          <span className="text-[var(--alert)]">-{dels}</span>
        </span>
        <button onClick={publishThis} disabled={published} data-testid="publish-preset-btn"
          className="h-7 px-2.5 rounded-[3px] border text-[11px] font-mono transition-colors disabled:opacity-50 border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)]/10">
          {published ? "✓ Published to all clients" : "Publish to all clients"}
        </button>
        <span className="ml-auto text-[10px] font-mono text-[var(--text-dim)]">{f.name} → Civil 3D 2018 {f.ok ? "✓" : "· review"}</span>
      </div>
      <div className="overflow-auto max-h-64 font-mono text-[11px] leading-[1.5]" data-testid="diff-body">
        {rows.map((r, k) => (
          <div key={k} className={
            r.t === "add" ? "bg-[var(--success)]/12 text-[var(--success)] px-2 whitespace-pre-wrap break-words"
            : r.t === "del" ? "bg-[var(--alert)]/12 text-[var(--alert)] px-2 whitespace-pre-wrap break-words"
            : "text-[var(--text-dim)] px-2 whitespace-pre-wrap break-words"
          }>
            <span className="select-none opacity-60">{r.t === "add" ? "+ " : r.t === "del" ? "- " : "  "}</span>{r.l || " "}
          </div>
        ))}
      </div>
    </div>
  );
};

export const LispLibrary = () => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  const [converting, setConverting] = useState(false);
  const [convJob, setConvJob] = useState(null);
  const [convStage, setConvStage] = useState("");
  const [convDone, setConvDone] = useState(null);
  const [catFilter, setCatFilter] = useState("all");

  const load = () => api.libraryLisp().then(setInfo).catch(() => setInfo(null));

  useEffect(() => { if (open) load(); }, [open]);

  const convertAll = async () => {
    if (converting) return;
    setConverting(true); setConvDone(null); setConvStage("Starting…");
    try {
      const { job_id } = await api.libraryPort({ platform: "AutoCAD Civil 3D", version: "2018" });
      setConvJob(job_id);
      const started = Date.now();
      while (Date.now() - started < 1200000) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await api.portLispStatus(job_id);
        if (s.progress?.stage) setConvStage(s.progress.stage);
        if (s.status === "done") { setConvDone(s.result); toast.success("Library converted to Civil 3D 2018"); break; }
        if (s.status === "error") { toast.error(s.error || "Convert failed"); break; }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Convert failed"); }
    finally { setConverting(false); }
  };

  const downloadConverted = async () => {
    if (!convJob) return;
    try {
      const blob = await api.portLispZip(convJob);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "house_library_Civil3D2018.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const upload = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    setBusy(true);
    const t = toast.loading(`Uploading ${files.length} LISP file${files.length > 1 ? "s" : ""}…`);
    try {
      const r = await api.uploadLibraryLisp(files);
      toast.success(`Added ${r.count} file${r.count === 1 ? "" : "s"} to the shared LISP library`, { id: t });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed", { id: t });
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from the shared library?`)) return;
    try { await api.deleteLibraryLisp(id); toast.success(`Removed "${name}"`); await load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="library-lisp-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <Stack size={14} weight="bold" /> LISP Library
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px] max-h-[90vh] flex flex-col" data-testid="library-lisp-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-mono text-base flex items-center gap-2">
            <Stack size={18} weight="bold" className="text-[var(--primary)]" /> Shared LISP Reference Library
          </DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">
            Admin-only. Upload your existing routines (progeCAD <b>and</b> AutoCAD Civil 3D). Every command here is read by the AI and reused as reference when building new routines for <b>any</b> client — progeCAD routines are ported to the client's target (Civil 3D 2018) using their template layers.
          </DialogDescription>
        </DialogHeader>

        <input ref={inputRef} type="file" multiple accept=".lsp,.lisp,.txt" className="hidden" onChange={upload} data-testid="library-file-input" />

        <div className="shrink-0 flex items-center gap-2">
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="library-upload-btn"
            className="h-9 rounded-[3px] gap-1.5 text-xs bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            {busy ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} weight="bold" />}
            Upload .lsp files (multiple)
          </Button>
          {info && (
            <span className="text-[11px] font-mono text-[var(--text-dim)]" data-testid="library-summary">
              {info.total_files} file{info.total_files === 1 ? "" : "s"} · {info.total_commands} command{info.total_commands === 1 ? "" : "s"} in the reference set
            </span>
          )}
        </div>

        {info?.total_files > 0 && (
          <div className="shrink-0 flex items-center gap-2 flex-wrap border border-[var(--primary)]/30 bg-[var(--primary)]/5 rounded-[4px] p-2.5 mt-2" data-testid="library-convert-panel">
            <span className="text-[11px] font-mono text-[var(--text)] flex items-center gap-1.5">
              <ArrowsClockwise size={13} weight="bold" className="text-[var(--primary)]" />
              Convert the whole library to a ready-to-load pack:
            </span>
            <Button size="sm" onClick={convertAll} disabled={converting} data-testid="library-convert-btn"
              className="h-8 rounded-[3px] gap-1.5 text-xs bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
              {converting ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} weight="bold" />}
              Convert all → Civil 3D 2018
            </Button>
            {converting && <span className="text-[11px] font-mono text-[var(--text-dim)]" data-testid="library-convert-stage">{convStage}</span>}
            {convDone && (
              <Button size="sm" variant="outline" onClick={downloadConverted} data-testid="library-convert-download"
                className="h-8 rounded-[3px] gap-1.5 text-xs border-[var(--success)]/60 text-[var(--success)] hover:bg-[var(--success)]/10">
                <UploadSimple size={13} weight="bold" className="rotate-180" /> Download Civil 3D 2018 pack (.zip)
              </Button>
            )}
          </div>
        )}
        {convDone?.files?.length > 0 && <ConvertDiff files={convDone.files} />}

        {info?.files?.length > 0 && (
          <div className="shrink-0 flex items-center gap-2 mt-2" data-testid="library-cat-filter-row">
            <span className="text-[10px] font-mono text-[var(--text-dim)] uppercase tracking-wider">Filter</span>
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="library-cat-filter"
              className="h-7 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] text-[11px] font-mono px-1.5 focus:outline-none">
              <option value="all">All categories</option>
              {LIB_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        <ScrollArea className="flex-1 min-h-0 mt-2 border border-[var(--border)] rounded-[4px]">
          {!info?.files?.length ? (
            <div className="p-4 text-[12px] text-[var(--text-dim)] leading-relaxed">
              No shared LISP uploaded yet. Add your library of production routines here so the AI reuses their exact command names and workflow across all clients.
            </div>
          ) : (
            info.files.filter((f) => catFilter === "all" || (f.category || "uncategorized") === catFilter).map((f) => (
              <div key={f.id} className="group flex items-start justify-between gap-2 px-3 py-2.5 border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)] transition-colors" data-testid={`library-file-${f.id}`}>
                <div className="min-w-0">
                  <div className="text-[13px] truncate flex items-center gap-1.5">
                    <LockSimple size={11} className="text-[var(--text-dim)] shrink-0" /> {f.name}
                  </div>
                  <div className="text-[10px] text-[var(--text-dim)] font-mono mt-1 flex flex-wrap items-center gap-1">
                    {(f.type || "").toUpperCase()} · {f.commands.length} command{f.commands.length === 1 ? "" : "s"}
                    {f.commands.slice(0, 12).map((c) => (
                      <span key={c} className="inline-block font-mono text-[var(--primary)] bg-[var(--primary)]/10 rounded-[2px] px-1">c:{c}</span>
                    ))}
                    {f.commands.length > 12 && <span>+{f.commands.length - 12} more</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <select value={f.category || "uncategorized"} data-testid={`library-cat-${f.id}`}
                    onChange={async (e) => { try { await api.setLibraryCategory(f.id, e.target.value); await load(); } catch { toast.error("Could not tag"); } }}
                    className="h-7 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] text-[10px] font-mono px-1 focus:outline-none">
                    <option value="uncategorized">tag…</option>
                    {LIB_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button onClick={() => del(f.id, f.name)} data-testid={`library-delete-${f.id}`}
                    className="opacity-0 group-hover:opacity-100 text-[var(--text-dim)] hover:text-[var(--alert)] transition-opacity">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </ScrollArea>
        <div className="shrink-0 flex items-center gap-1.5 text-[10px] text-[var(--text-dim)] font-mono pt-1">
          <ArrowsClockwise size={12} /> Changes take effect on the next generation for every client.
        </div>
      </DialogContent>
    </Dialog>
  );
};
